/**
 * Stacked daily bar chart with hover: one band per series, the day under
 * the pointer lights up and a tooltip lists every band's value for that
 * day. Sibling of `HoverBars` for the site-level "traffic by agent" view.
 */

import { useState } from 'react';

import { useC } from '../../lib/theme.js';

const MONO = "'JetBrains Mono', monospace";

/** Band palette: the semantic accents in their theme-correct form (the
 *  `*Fg` tokens are the bright hues in dark mode and the deep readable
 *  ones in light mode), then a violet. Beyond the length bands cycle. */
/** Default count formatter: thousands separators (3,455 not 3455). */
const formatCount = (n: number): string => n.toLocaleString('en-US');

export function useBandColors(): readonly string[] {
  const C = useC();
  return [C.limeFg, C.skyFg, C.amberFg, C.mintFg, C.coralFg, '#8A6BE0'];
}

export interface StackedSeries {
  readonly label: string;
  readonly values: readonly number[];
  /** Explicit colour; default from `useBandColors()` by index. */
  readonly color?: string | undefined;
}

export interface StackedBarsProps {
  readonly days: readonly string[];
  readonly series: readonly StackedSeries[];
  readonly width: number;
  readonly height: number;
  readonly formatDay: (day: string) => string;
  readonly formatValue?: ((n: number) => string) | undefined;
  readonly 'aria-label'?: string | undefined;
}

export function StackedBars({ days, series, width: W, height: H, formatDay, formatValue = formatCount, 'aria-label': ariaLabel }: StackedBarsProps): JSX.Element {
  const C = useC();
  const [hover, setHover] = useState<number | null>(null);
  const axisH = 14;
  const plotH = H - axisH;
  const pad = 4;
  const n = Math.max(1, days.length);
  const bw = (W - pad * 2) / n;
  const totals = days.map((_, i) => series.reduce((s, b) => s + (b.values[i] ?? 0), 0));
  const max = Math.max(1, ...totals);
  const palette = useBandColors();
  const colorOf = (i: number): string => series[i]?.color ?? palette[i % palette.length]!;
  const tipRight = hover !== null && hover > n / 2;

  return (
    <div style={{ position: 'relative', width: '100%', userSelect: 'none' }} data-testid="stacked-bars">
      <svg
        viewBox={`0 0 ${W} ${H}`}
        width="100%"
        height={H}
        preserveAspectRatio="none"
        role="img"
        aria-label={ariaLabel}
        style={{ display: 'block', cursor: 'crosshair', overflow: 'visible' }}
        onMouseLeave={() => setHover(null)}
      >
        <line x1={pad} x2={W - pad} y1={plotH} y2={plotH} stroke={C.border} strokeWidth={1} />
        {days.map((d, i) => {
          const x = pad + i * bw;
          const on = hover === i;
          let y = plotH;
          return (
            <g key={d} onMouseEnter={() => setHover(i)} onTouchStart={() => setHover(i)}>
              <rect x={x} y={0} width={bw} height={plotH} fill={on ? C.surface2 : 'transparent'} />
              {series.map((s, si) => {
                const v = s.values[i] ?? 0;
                if (v <= 0) return null;
                const h = Math.max(1, Math.round((plotH - 2) * (v / max)));
                y -= h;
                return <rect key={si} x={x + 0.5} y={y} width={Math.max(1, bw - 1)} height={h} fill={colorOf(si)} opacity={on ? 1 : 0.8} />;
              })}
            </g>
          );
        })}
        {days.length > 0 ? (
          <>
            <text x={pad} y={H - 3} fontFamily={MONO} fontSize={9} fill={C.muted}>{formatDay(days[0]!)}</text>
            <text x={W - pad} y={H - 3} fontFamily={MONO} fontSize={9} fill={C.muted} textAnchor="end">{formatDay(days[days.length - 1]!)}</text>
          </>
        ) : null}
      </svg>
      {hover !== null ? (
        <div
          role="tooltip"
          style={{
            position: 'absolute',
            top: -4,
            ...(tipRight ? { right: `${100 - ((pad + (hover + 0.5) * bw) / W) * 100}%` } : { left: `${((pad + (hover + 0.5) * bw) / W) * 100}%` }),
            transform: 'translateY(-100%)',
            background: C.surface2,
            border: `1px solid ${C.border}`,
            borderRadius: 2,
            padding: '6px 9px',
            fontFamily: MONO,
            fontSize: 10,
            lineHeight: 1.6,
            color: C.text,
            whiteSpace: 'nowrap',
            pointerEvents: 'none',
            zIndex: 20,
            boxShadow: '0 4px 12px rgba(0,0,0,0.18)',
          }}
        >
          <div style={{ color: C.muted, fontSize: 9, letterSpacing: 1, textTransform: 'uppercase' }}>{formatDay(days[hover]!)} · {formatValue(totals[hover] ?? 0)}</div>
          {series.map((s, si) => {
            const v = s.values[hover] ?? 0;
            if (v <= 0) return null;
            return (
              <div key={si} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ width: 8, height: 8, background: colorOf(si), borderRadius: 1, display: 'inline-block' }} />
                <span style={{ color: C.muted }}>{s.label}</span>
                <span style={{ marginLeft: 'auto', paddingLeft: 10 }}>{formatValue(v)}</span>
              </div>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}
